<template>
  <vs-row class="p-3 mt-2 shadow-sm bg-white">
    <vs-col vs-lg="11">
      <vs-row vs-type="flex" vs-align="center">
        <vs-col class="text-primary" vs-lg="6">
          <div class="d-flex align-items-center">
            <font-awesome-icon
              :icon="['fas', fileTypeIcon({ name: doc.DOCNAME })]"
              :class="fileTypeIcon({ name: doc.DOCNAME })"
              class="mr-2"
              style="font-size: 20px;"
            />
            <div>
              <span>{{ doc.DOCNAME }}</span>
              <small class="d-block">{{ displayTypeDesc }}</small>
            </div>
          </div>
        </vs-col>
        <vs-col class="text-primary" vs-lg="5">
          {{ displayAuthor }}
        </vs-col>
      </vs-row>
      <vs-row>
        <vs-col vs-lg="12" vs-type="flex" class="pt-2 border-top chips-row">
          <vs-chip class="mr-2 mb-1 folio-chip" v-if="displayFolio" v-tooltip="'Folio: ' + displayFolio" @dblclick.native="copyFolio">
            <vs-avatar icon="tag" color="#ff9f43"/>
            {{ truncate(displayFolio, 40) }}
          </vs-chip>
          <vs-chip
            class="mr-2 mb-1"
            v-if="doc.DOCUMENTO_MADRE"
            v-tooltip="'Documento Madre: ' + doc.DOCUMENTO_MADRE"
          >
            <vs-avatar icon="supervisor_account" color="#8633FF" />
            {{ truncate(doc.DOCUMENTO_MADRE, 14) }}
          </vs-chip>
          <vs-chip
            class="mr-2 mb-1"
            v-if="displayFechaDocumento"
            v-tooltip="'Fecha de documento'"
          >
            <vs-avatar icon="date_range"/>
            {{ displayFechaDocumento | dateFormat }}
          </vs-chip>
          <vs-chip class="mr-2 mb-1" v-tooltip="'Fecha de creacion'">
            <vs-avatar icon="date_range" />
            {{ doc.CREATION_DATE | dateFormat }}
          </vs-chip>
          <vs-chip class="mr-4" v-if="doc.LAST_EDIT_DATE" v-tooltip="'Fecha de actualización'">
            <vs-avatar icon="date_range" />
            {{ doc.LAST_EDIT_DATE | dateFormat }}
          </vs-chip>
          <vs-chip
            class="mr-2 mb-1"
            v-tooltip="'Autor'"
            v-if="displayIdAutor"
          >
            <vs-avatar icon="business" color="#FF0000"/>
            {{ displayIdAutor }}
          </vs-chip>
          <vs-chip
            class="mr-2 mb-1"
            v-tooltip="'Empresa'"
            v-if="displayEmpresa"
          >
            <vs-avatar icon="business" color="#003287"/>
            {{ displayEmpresa }}
          </vs-chip>
          <vs-chip
            class="mr-2 mb-1"
            v-tooltip="'Tema'"
            v-if="displayTema"
          >
            <vs-avatar icon="label" color="#7367f0"/>
            {{ truncate(displayTema, 16) }}
          </vs-chip>
          <vs-chip
            class="mr-2 mb-1"
            v-tooltip="'Subtema'"
            v-if="doc.ID_SUBTEMA"
          >
            <vs-avatar icon="edit" color="#ff9f43"/>
            {{ truncate(doc.ID_SUBTEMA, 16) }}
          </vs-chip>
          <vs-chip
            class="mr-2 mb-1"
            v-tooltip="'Materia: ' + doc.MATERIA_REG"
            v-if="doc.MATERIA_REG"
          >
            <vs-avatar icon="subject" color="#00cfe8"/>
            {{ truncate(doc.MATERIA_REG, 20) }}
          </vs-chip>
          <vs-chip color="success" class="mr-2 mb-1" v-tooltip="'Tiene documento adjunto'">
            <vs-avatar icon="description" color="success" />
            <span v-if="!hasAdjunto">sin adjunto</span>
            <span v-else>con adjunto</span>
          </vs-chip>
          <vs-chip color="success" class="mr-2 mb-1" v-tooltip="'Tipo de documento'" v-if="displayTipoDocumento">
            <vs-avatar icon="insert_drive_file" color="success" />
            {{ truncate(displayTipoDocumento, 12) }}
          </vs-chip>
          <!-- Campos adicionales de eGestion -->
          <vs-chip
            class="mr-2 mb-1"
            v-if="isEgestion && displayFileName"
            v-tooltip="'Nombre archivo: ' + displayFileName"
          >
            <vs-avatar icon="attach_file" color="#5b3cc4"/>
            {{ truncate(displayFileName, 24) }}
          </vs-chip>
          <vs-chip
            class="mr-2 mb-1"
            v-if="isEgestion && doc.UNITY"
            v-tooltip="'Unidad: ' + doc.UNITY"
          >
            <vs-avatar icon="apartment" color="#0277bd"/>
            {{ truncate(doc.UNITY, 16) }}
          </vs-chip>
          <vs-chip
            class="mr-2 mb-1"
            v-if="isEgestion && doc.SIZE"
            v-tooltip="'Tamano'"
          >
            <vs-avatar icon="save" color="#777"/>
            {{ displayFileSize }}
          </vs-chip>
        </vs-col>
      </vs-row>
    </vs-col>
    <vs-col vs-lg="1">
      <div class="action" v-if="doc.DOCNUM" v-on:click="$emit('download', doc.DOCNUM)">
        <font-awesome-icon :icon="['fas', 'download']" class="action-icon"/>
      </div>
    </vs-col>
  </vs-row>
</template>

<script>
import EDIcons from '@/febos/global/documentManagement/mixins/EDIcons';

const STATE_LABELS = {
  draft: 'Borrador',
  signed: 'Firmado',
  nulled: 'Anulado',
  digitized: 'Digitalizado',
  rejected: 'Rechazado'
};

export default {
  name: 'OpenTextRow',
  mixins: [EDIcons],
  props: {
    doc: {
      type: Object,
      required: true
    },
    source: {
      type: String,
      default: null
    }
  },
  computed: {
    isEgestion() {
      return this.source === 'egestion';
    },
    raw() {
      return this.doc._raw || this.doc.raw || {};
    },
    meta() {
      return this.raw.documentMeta || {};
    },
    displayDocNum() {
      if (this.isEgestion) {
        // Mostrar hash corto para eGestión
        const hash = this.doc.DOCNUM || '';
        return hash.length > 12 ? `${hash.substring(0, 12)}...` : hash;
      }
      return this.doc.DOCNUM;
    },
    displayAuthor() {
      return this.doc.AUTHOR_FULL_NAME || this.meta.ID_AUTOR || '-';
    },
    displayTypeDesc() {
      if (this.isEgestion) {
        return this.doc?.raw?.documentMeta?.TYPE_ID || this.doc.TEMPLATE || this.doc.TYPE_DESC || '';
      }
      return this.doc.TYPE_DESC || '';
    },
    displayTema() {
      return this.doc.ID_TEMA || this.meta.ID_TEMA || '';
    },
    displayFechaDocumento() {
      return this.doc.CAMPO45 || this.doc.CAMPO48 || this.meta.CAMPO48 || '';
    },
    displayIdAutor() {
      return this.doc.ID_AUTOR || this.meta.ID_AUTOR || '';
    },
    displayEmpresa() {
      if (this.isEgestion) {
        return this.doc.ID || this.meta.ID || '';
      }
      return this.doc.ID || '-';
    },
    displayFolio() {
      if (this.isEgestion) {
        return this.doc.FOLIO || this.meta.edFolio || this.meta.clientDocumentId || '';
      }
      return this.doc.FOLIO || '';
    },
    hasAdjunto() {
      if (this.isEgestion) {
        const adj = this.doc.DOCUMENTO_ADJUNTO || this.meta.DOCUMENTO_ADJUNTO || 'NO';
        return adj === 'SI';
      }
      return this.doc.DOCUMENTO_ADJUNTO && this.doc.DOCUMENTO_ADJUNTO !== 'NO';
    },
    displayTipoDocumento() {
      if (this.isEgestion) {
        return this.doc.TEMPLATE || this.meta.TYPE_ID || this.doc.TYPE_ID || '';
      }
      return this.doc.TYPE_ID || '';
    },
    displayFileName() {
      return this.meta.FILENAME || this.doc.FILENAME || this.raw.filename || '';
    },
    displayState() {
      const state = this.doc.STATE || '';
      return STATE_LABELS[state] || state;
    },
    stateColor() {
      const state = this.doc.STATE || '';
      if (state === 'signed') return 'success';
      if (state === 'nulled' || state === 'rejected') return 'danger';
      return 'warning';
    },
    stateIcon() {
      const state = this.doc.STATE || '';
      if (state === 'signed') return 'check_circle';
      if (state === 'nulled' || state === 'rejected') return 'cancel';
      return 'schedule';
    },
    displayFileSize() {
      const bytes = parseInt(this.doc.SIZE, 10);
      if (isNaN(bytes)) return '';
      if (bytes < 1024) return `${bytes} B`;
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
      return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    }
  },
  methods: {
    truncate(text, maxLength) {
      if (!text) return '';
      if (text.length <= maxLength) return text;
      return `${text.substring(0, maxLength)}...`;
    },
    copyFolio() {
      const folio = this.displayFolio;
      if (folio) {
        navigator.clipboard.writeText(folio);
      }
    }
  }
};
</script>

<style scoped>
.chips-row {
  flex-wrap: wrap;
  gap: 2px 0;
}

/* El folio no se restringe de ancho para mostrar el codigo completo */
.folio-chip {
  max-width: none;
}

.folio-chip .vs-chip--text {
  max-width: none;
  overflow: visible;
  text-overflow: unset;
}
</style>
